import * as _ from 'lodash';
import { User as UserClaims } from '../auth/me.lib';
import { formatText, isNotNull, zeroPadNumber } from '../common/utils.lib';
import {
  ItemModes,
  StoryLabels, StoryPoints, StoryPriorities, StoryStates, StoryTypes, UserStoryReviewStatus, UserStoryReviewTypes

} from '../common/constants.lib';
// import { isNotNull } from "@casi-ts/utils";

export function setDefaultStatus(statuses: CasiStoryStatus[] = [], defaultStr: string = 'Not Started'): CasiStoryStatus | undefined {
  if (statuses.length === 0) {
    throw new Error(`setDefaultStatus: expected array of statuses`);
  }
  return statuses.find(s => s.user_story_status_description.toLowerCase() === defaultStr.toLowerCase());
}

export interface IIdish {
  id: number
};

export interface IRole {
  id?: number,
  name?: string,
  permission_cnt?: string
};

export class Role implements IRole {
  static create(args: any) {
    const {
      id, name, permission_cnt
    } = args;
    return new Role(id, name, permission_cnt);
  }
  constructor(
    public id?: number,
    public name?: string,
    public permission_cnt?: string
  ) {
  }

  get permissionCnt(): number {
    if (this.permission_cnt)
      return +this.permission_cnt;
    return 0;
  }
};

export interface IPermissionNew {
  entity?: string;
  action?: string;
};

export class PermissionNew implements IPermissionNew {
  static create() {
    return new PermissionNew();
    // return new PermissionNew('', '');
  }
  constructor(
    public entity?: string,
    public action?: string) { }

};

export interface IPermission extends IPermissionNew {
  id: number;
  selected?: boolean;
};

export class Permission implements IPermission {
  static create(args: IPermission) {
    const {
      id, entity, action
    } = args;
    return new Permission(id, entity, action);
  }

  constructor(
    public id: number,
    public entity?: string,
    public action?: string,
    public selected: boolean = false
  ) { }

  set select(val: boolean) {
    this.selected = val;
  }
  get select() {
    return this.selected;
  }

  getRolePermission(roleId: number): RolePermission | undefined {
    if (this.id)
      return new RolePermission(roleId, this.id);
    return undefined;
  }
};

export type PermissionType = PermissionNew | Permission;

export interface IUserDbNew {
  user_id: number;
  user?: string;
  project_id?: number;
  project_name?: string;
  database_name?: string;
  description?: string;
  no_updates_due?: number;
  host?: string;
  port?: number;
  use_ssh_tunnel?: boolean;
  tunnel_port?: number;
  tunnel_user_name?: string;
  tunnel_destination_host?: string;
};

export class UserDbNew implements IUserDbNew {
  static createNew(userId: number) {
    return new UserDbNew(userId);
  }
  constructor(
    public user_id: number,
    public project_id?: number,
    public database_name?: string,
    public description?: string,
    public no_updates_due?: number,
    public host?: string,
    public port?: number,
    public use_ssh_tunnel?: boolean,
    public tunnel_port?: number,
    public tunnel_user_name?: string,
    public tunnel_destination_host?: string,
    public user?: string,
    public project_name?: string
  ) { }
};

export interface IUserDb extends IUserDbNew {
  id: number;
};

export type UserDbType = IUserDbNew | IUserDb;

export class UserDb implements IUserDb {
  static create(args: IUserDb) {
    const { id, user_id, project_id, database_name,
      description, no_updates_due,
      host, port, use_ssh_tunnel,
      tunnel_port,
      tunnel_user_name,
      tunnel_destination_host,
      user, project_name
    } = args;
    return new UserDb(
      id,
      user_id,
      project_id,
      database_name,
      description,
      no_updates_due,
      host,
      port,
      use_ssh_tunnel,
      tunnel_port,
      tunnel_user_name,
      tunnel_destination_host,
      user, project_name
    )
  }
  static from(item: IUserDb, projects: Project[], users: User[]) {
    const { user_id, project_id,
    } = item;
    // console.log(`from:user_id: `, user_id);
    if (isNotNull(user_id) && users.length > 0) {
      let found = users.find(u => u.id === user_id);
      // console.log(`from:user:found: `, found);
      if (isNotNull(found)) {
        item.user = found?.email;
      }
    }
    if (isNotNull(project_id) && projects.length > 0) {
      let found = projects.find(p => p.id === project_id);
      if (isNotNull(found)) {
        item.project_name = found?.project_name;
      }
    }
    // console.log('from:item: ', item);
    return UserDb.create(item);
  }

  constructor(
    public id: number,
    public user_id: number,
    public project_id?: number,
    public database_name?: string,
    public description?: string,
    public no_updates_due?: number,
    public host?: string,
    public port?: number,
    public use_ssh_tunnel?: boolean,
    public tunnel_port?: number,
    public tunnel_user_name?: string,
    public tunnel_destination_host?: string,
    public user?: string,
    public project_name?: string
  ) {
  }
}

export interface IUserDbToProcess {
  id: number;
  implemention_order: number;
  project_id: number;
  file_name: string;
  update_script: string;
  updateScriptDisplay?: string | null;
  submission_email_address: string;
  description: string | null;
  selected: boolean | null;
  project_name?: string | null;
};

export class UserDbToProcess implements IUserDbToProcess {
  public updateScriptDisplay?: string | undefined | null;

  static create(args: IUserDbToProcess) {
    const {
      id, implemention_order,
      project_id, project_name,
      file_name, update_script,
      submission_email_address,
      description, selected
    } = args;

    return new UserDbToProcess(id,
      implemention_order, project_id,
      file_name, update_script, submission_email_address,
      description, selected,
      project_name
    );
  }

  static from(item: IUserDbToProcess, projects: Project[]) {
    const { project_id,
    } = item;
    if (isNotNull(project_id) && projects.length > 0) {
      let found = projects.find(p => p.id === project_id);
      if (isNotNull(found)) {
        item.project_name = found?.project_name;
      }
    }

    return UserDbToProcess.create(item);
  }

  constructor(
    public id: number,
    public implemention_order: number,
    public project_id: number,
    public file_name: string,
    public update_script: string,
    public submission_email_address: string,
    public description: string | null,
    public selected: boolean | null,
    public project_name?: string | null,
  ) {
    if (update_script && update_script.length > 30) {
      this.updateScriptDisplay = `${update_script.substring(0, 30)}...`;
    } else {
      this.updateScriptDisplay = update_script;
    }
    this.selected = true;
  }
};

export interface IDbUpdateNew {
  project_id: number;
  description?: string | null;
  file_name?: string | null;
  update_script?: string | null;
  updateScriptDisplay?: string | null;
  created_by_user_id?: number | null;
  approved_by_user_id?: number | null;
  approved_on: Date | null;
  approved_by_user?: string | null;
  implementation_order?: number | null;
  created_by_user?: string | null;
  project_name?: string | null;
};

export class DbUpdateNew implements IDbUpdateNew {
  public updateScriptDisplay?: string | undefined | null;

  static create(project: Project, user: UserClaims) {
    return new DbUpdateNew(
      project.id,
      null,
      null,
      null,
      user.userId,
      null,
      null,
      1,
      user.email,
      project.project_name,
      null
    );
  }
  constructor(
    public project_id: number,
    public description?: string | null,
    public file_name?: string | null,
    public update_script?: string | null,
    public created_by_user_id?: number | null,
    public approved_by_user_id?: number | null,
    public approved_on: Date | null = null,
    public implementation_order?: number | null,
    public created_by_user?: string | null,
    public project_name?: string | null,
    public approved_by_user?: string | null
  ) {
    if (update_script && update_script.length > 30) {
      this.updateScriptDisplay = `${update_script.substring(0, 30)}...`;
    } else {
      this.updateScriptDisplay = update_script;
    }
  }
};

export interface IDbUpdate extends IDbUpdateNew {
  id: number;
  isOwner?: boolean | null;
  wasAlreadyApproved?: boolean | null;
  isAdmin?: boolean | null;
  approvedOn?: string | null;
};

export type DbUpdateType = IDbUpdateNew | IDbUpdate;

export class DbUpdate implements IDbUpdate {
  public updateScriptDisplay?: string | undefined | null;

  static create(args: IDbUpdate) {
    const {
      id, project_id, description, file_name,
      update_script, created_by_user, created_by_user_id,
      approved_by_user, approved_by_user_id, approved_on,
      implementation_order, project_name,
      isOwner, wasAlreadyApproved, isAdmin,
      approvedOn
    } = args;
    return new DbUpdate(
      id,
      project_id,
      description,
      file_name,
      update_script,
      created_by_user_id,
      approved_by_user_id,
      approved_on ? new Date(approved_on) : approved_on,
      implementation_order,
      isOwner,
      wasAlreadyApproved,
      isAdmin,
      approvedOn,
      created_by_user,
      approved_by_user,
      project_name
    );
  }

  static from(item: IDbUpdate, projects: Project[], users: User[], me: UserClaims) {
    const { project_id, created_by_user_id, approved_by_user_id } = item;
    const { userId, admin } = me;
    if (isNotNull(project_id) && projects.length > 0) {
      let found = projects.find(p => p.id === project_id);
      if (isNotNull(found)) {
        item.project_name = found!.project_name;
      }
    }
    if (isNotNull(created_by_user_id) && users.length > 0) {
      let found = users.find(u => u.id === created_by_user_id);
      if (isNotNull(found)) {
        item.created_by_user = found!.email;
      }
    }
    if (isNotNull(approved_by_user_id) && users.length > 0) {
      let found = users.find(u => u.id === approved_by_user_id);
      if (isNotNull(found)) {
        item.approved_by_user = found!.email;
      }
    }
    // console.log(`tmpUser`, item, item?.approved_on, typeof item?.approved_on);
    item.approvedOn = item.approved_on ? new Date(item?.approved_on).toLocaleDateString() : null; //.format('DD/MM/YYYY') : null;
    // item.approvedOn = item.approved_on ? item?.approved_on.toLocaleDateString() : null; //.format('DD/MM/YYYY') : null;
    item.wasAlreadyApproved = isNotNull(approved_by_user_id);
    item.isOwner = isNotNull(userId) ? userId === item.created_by_user_id : false;
    item.isAdmin = admin || false;
    return DbUpdate.create(item);
  }

  constructor(
    public id: number,
    public project_id: number,
    public description?: string | null,
    public file_name?: string | null,
    public update_script?: string | null,
    public created_by_user_id?: number | null,
    public approved_by_user_id?: number | null,
    public approved_on: Date | null = null,
    public implementation_order?: number | null,
    public isOwner?: boolean | null,
    public wasAlreadyApproved?: boolean | null,
    public isAdmin?: boolean | null,
    public approvedOn?: string | null,
    public created_by_user?: string | null,
    public approved_by_user?: string | null,
    public project_name?: string | null
  ) {
    if (update_script && update_script.length > 30) {
      this.updateScriptDisplay = `${update_script.substring(0, 30)}...`;
    } else {
      this.updateScriptDisplay = update_script;
    }
  }
};

export interface IUserDbUpdate {
  id: number;
  project_name?: string | null;
  contact_person?: string | null;
  contact_email?: string | null;
  database_name?: string | null;
  description?: string | null;
  file_name?: string | null;
  // update_script?: string | null;
  user_db_id?: number | null;
  db_update_id?: number | null;
  // updateScriptDisplay?: string | null;
};

export class UserDbUpdate implements IUserDbUpdate {
  // public updateScriptDisplay?: string | undefined | null;

  static create(args: IUserDbUpdate) {
    const {
      id,
      project_name,
      contact_person, contact_email,
      database_name, description,
      file_name, // update_script,
      user_db_id, db_update_id
    } = args;
    return new UserDbUpdate(
      id, project_name,
      contact_person, contact_email,
      database_name, description,
      file_name, // update_script,
      user_db_id, db_update_id
    );
  }

  constructor(
    public id: number,
    public project_name?: string | null,
    public contact_person?: string | null,
    public contact_email?: string | null,
    public database_name?: string | null,
    public description?: string | null,
    public file_name?: string | null,
    // public update_script?: string | null,
    public user_db_id?: number | null,
    public db_update_id?: number | null
  ) {
    // if (update_script && update_script.length > 30) {
    //   this.updateScriptDisplay = `${update_script.substring(0, 30)}...`;
    // } else {
    //   this.updateScriptDisplay = update_script;
    // }
  }
}

export interface IRolePermission {
  role_id: number,
  permission_id: number
};

export class RolePermission implements IRolePermission {
  static createFrom(role: Role, permission: Permission) {
    return new RolePermission((role.id as number), permission.id);
  }

  static create(args: any) {
    const { role_id, permission_id } = args;
    return new RolePermission(
      role_id,
      permission_id
    );
  }

  constructor(
    public role_id: number,
    public permission_id: number
  ) { }
}

export class RolePermissionSelectable {
  constructor() { }
}

export interface IUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  research_consent?: boolean;
  accepted_terms_version?: number;
  userRoles?: any[];
  fullName?: string;
  initials?: string;
  active?: boolean;
};

export class User implements IUser {
  static create(args: any) {
    const {
      id, first_name, last_name, phone,
      email, research_consent,
      accepted_terms_version, active
    } = args;
    return new User(
      id,
      email, phone,
      first_name, last_name,
      research_consent, accepted_terms_version, active
    );
  }

  public userRoles: any[] = [];
  // public fullName?: string;
  // public intitials?: string;
  public user_label: string;

  constructor(
    public id?: number,
    public email: string = '',
    public phone?: string,
    public first_name?: string,
    public last_name?: string,
    public research_consent?: boolean,
    public accepted_terms_version?: number,
    public active?: boolean
  ) {
    this.userRoles = [];
    this.user_label = `${this.first_name} ${this.last_name} (${this.email})`;
    // if (this.first_name && this.last_name) {
    //   this.fullName = `${this.first_name} ${this.last_name}`;
    // } else if (this.first_name) {
    //   this.fullName = this.first_name;
    // } else if (this.last_name) {
    //   this.fullName = this.last_name
    // } else {
    //   this.fullName = `fullName not set`;
    // }
    // if (this.first_name && this.last_name) {
    //   this.intitials = `${this.first_name.charAt(0).toUpperCase()} ${this.last_name.charAt(0).toLocaleUpperCase()}`;
    // } else if (this.first_name) {
    //   this.intitials = this.first_name.charAt(0).toUpperCase();
    // } else if (this.last_name) {
    //   this.intitials = this.last_name.charAt(0).toLocaleUpperCase()
    // } else {
    //   this.intitials = `NS`;
    // }

  }

  get fullName() {
    if (this.first_name && this.last_name) {
      return `${this.first_name} ${this.last_name}`;
    } else if (this.first_name) {
      return this.first_name;
    } else if (this.last_name) {
      return this.last_name
    } else {
      return `fullName not set`;
    }
  }

  get initials() {
    if (this.first_name && this.last_name) {
      return `${this.first_name.charAt(0).toUpperCase()}${this.last_name.charAt(0).toLocaleUpperCase()}`;
    } else if (this.first_name) {
      return this.first_name.charAt(0).toUpperCase();
    } else if (this.last_name) {
      return this.last_name.charAt(0).toLocaleUpperCase()
    } else {
      return `NS`;
    }
  }

  addRoles(roles: any[]) {
    this.userRoles = [...roles];
  }
};

export interface IUserRole {
  role_id: number
};

export class UserRole implements IUserRole {
  static create(args: any) {
    const { role_id } = args;
    return new UserRole(role_id);
  }
  constructor(
    public role_id: number
  ) { }
};

export interface MayHaveId {
  id?: number;
  checked?: boolean;
  description?: string;
}

export interface ICasiControl {
  db_major_version?: number;
  db_minor_version?: number;
  app_base_url?: string;
  param_company_name?: string;
  param_entity_name?: string;
  param_company_address?: string;
  param_abn?: string;
  param_acn?: string;
  param_primary_email?: string;
  param_additional_email?: string;
  param_website_url?: string;
  param_primary_contact_number?: number;
  param_additional_contact_number?: number;
  param_product_name?: string;
};

export interface ICasiControlImage {
  id?: number;
  param_primary_image?: string;
  param_additional_image?: string;
  param_reply_to_email?: string;
  param_reset_password_bcc?: string;
};

export class CasiControl implements ICasiControl {
  static create(args: any) {
    const {
      db_major_version, db_minor_version, app_base_url,
      param_company_name, param_entity_name,
      param_company_address, param_abn, param_acn,
      param_primary_email, param_additional_email,
      param_website_url, param_primary_contact_number,
      param_additional_contact_number, param_product_name
    } = args;
    return new CasiControl(
      db_major_version, db_minor_version, app_base_url,
      param_company_name, param_entity_name,
      param_company_address, param_abn, param_acn,
      param_primary_email, param_additional_email,
      param_website_url, param_primary_contact_number,
      param_additional_contact_number, param_product_name
    );
  }

  constructor(
    public db_major_version?: number,
    public db_minor_version?: number,
    public app_base_url?: string,
    public param_company_name?: string,
    public param_entity_name?: string,
    public param_company_address?: string,
    public param_abn?: string,
    public param_acn?: string,
    public param_primary_email?: string,
    public param_additional_email?: string,
    public param_website_url?: string,
    public param_primary_contact_number?: number,
    public param_additional_contact_number?: number,
    public param_product_name?: string
  ) {
  }
}

export class CasiControlWithParams implements ICasiControl, ICasiControlImage {
  static create(args: any) {
    const {
      id,
      db_major_version, db_minor_version, app_base_url,
      param_company_name, param_entity_name,
      param_company_address, param_abn, param_acn,
      param_primary_email, param_additional_email,
      param_website_url, param_primary_contact_number,
      param_additional_contact_number, param_product_name,
      param_primary_image, param_additional_image,
      param_reply_to_email, param_reset_password_bcc
    } = args;
    return new CasiControlWithParams(
      id,
      db_major_version, db_minor_version, app_base_url,
      param_company_name, param_entity_name,
      param_company_address, param_abn, param_acn,
      param_primary_email, param_additional_email,
      param_website_url, param_primary_contact_number,
      param_additional_contact_number, param_product_name,
      param_primary_image, param_additional_image,
      param_reply_to_email, param_reset_password_bcc
    );
  }

  constructor(
    public id?: number,
    public db_major_version?: number,
    public db_minor_version?: number,
    public app_base_url?: string,
    public param_company_name?: string,
    public param_entity_name?: string,
    public param_company_address?: string,
    public param_abn?: string,
    public param_acn?: string,
    public param_primary_email?: string,
    public param_additional_email?: string,
    public param_website_url?: string,
    public param_primary_contact_number?: number,
    public param_additional_contact_number?: number,
    public param_product_name?: string,
    public param_primary_image?: string,
    public param_additional_image?: string,
    public param_reply_to_email?: string,
    public param_reset_password_bcc?: string
  ) {
  }
}

export interface IControlHost {
  db_major_version?: number;
  db_minor_version?: number;
  app_base_url?: string;
  param_company_name?: string,
  param_entity_name?: string;
  param_company_address?: string;
  param_abn?: string;
  param_acn?: string;
  param_primary_email?: string;
  param_additional_email?: string;
  param_website_url?: string;
  param_primary_contact_number?: string;
  param_additional_contact_number?: string;
  param_product_name?: string
};

export class ControlHost implements IControlHost {
  static create(args: any) {
    const {
      db_major_version, db_minor_version, app_base_url,
      param_company_name, param_entity_name,
      param_company_address,
      param_abn, param_acn,
      param_primary_email,
      param_additional_email,
      param_website_url,
      param_primary_contact_number,
      param_additional_contact_number,
      param_product_name
    } = args;
    return new ControlHost(
      db_major_version, db_minor_version, app_base_url,
      param_company_name, param_entity_name, param_company_address,
      param_abn, param_acn,
      param_primary_email,
      param_additional_email,
      param_website_url,
      param_primary_contact_number,
      param_additional_contact_number,
      param_product_name
    );
  }


  constructor(
    public db_major_version?: number,
    public db_minor_version?: number,
    public app_base_url?: string,
    public param_company_name?: string,
    public param_entity_name?: string,
    public param_company_address?: string,
    public param_abn?: string,
    public param_acn?: string,
    public param_primary_email?: string,
    public param_additional_email?: string,
    public param_website_url?: string,
    public param_primary_contact_number?: string,
    public param_additional_contact_number?: string,
    public param_product_name?: string
  ) {
  }

};

export interface ILevelDropDownTree {
  text: string;
  items: any[]
};

export class LevelDropDownTree implements ILevelDropDownTree {
  constructor(
    public text: string,
    public items: any[],
    public id: number | null
  ) { }
};

export interface IProjectNew {
  project_name?: string;
  contact_person?: string;
  contact_email?: string;
  contact_telephone?: string;
  funding_body?: string;
  project_amount?: number;
  development_amount?: number;
  dev_url?: string;
  uat_url?: string;
  prod_url?: string;
  postgresql_ver?: string;
};

export interface IProject extends IProjectNew {
  id: number;
};

export type ProjectType = IProjectNew | IProject;

export interface IProjectFromWeb {
  id: number;
  project_name?: string;
  contact_person?: string;
  contact_email?: string;
  contact_telephone?: string;
  funding_body?: string;
  project_amount?: string;
  development_amount?: string;
  dev_url?: string;
  uat_url?: string;
  prod_url?: string;
  postgresql_ver?: string;
  active_user_stories?: boolean;
  active_db_manager?: boolean;
};

export class ProjectNew implements IProjectNew {
  constructor(
    public project_name?: string,
    public contact_person?: string,
    public contact_email?: string,
    public contact_telephone?: string,
    public funding_body?: string,
    public project_amount?: number,
    public development_amount?: number,
    public dev_url?: string,
    public uat_url?: string,
    public prod_url?: string,
    public postgresql_ver?: string,
  ) { }
};

export class Project implements IProject {

  static create(args: IProjectFromWeb) {
    const {
      id, project_name,
      contact_email, contact_person, contact_telephone,
      funding_body,
      project_amount,
      development_amount,
      dev_url, uat_url, prod_url,
      postgresql_ver,
      active_user_stories,
      active_db_manager
    } = args;

    return new Project(id,
      project_name,
      contact_person,
      contact_email,
      contact_telephone,
      funding_body,
      project_amount ? +project_amount : undefined,
      development_amount ? +development_amount : undefined,
      dev_url,
      uat_url,
      prod_url,
      postgresql_ver,
      active_user_stories,
      active_db_manager
    )
  }

  constructor(
    public id: number,
    public project_name?: string,
    public contact_person?: string,
    public contact_email?: string,
    public contact_telephone?: string,
    public funding_body?: string,
    public project_amount?: number,
    public development_amount?: number,
    public dev_url?: string,
    public uat_url?: string,
    public prod_url?: string,
    public postgresql_ver?: string,
    public active_user_stories?: boolean,
    public active_db_manager?: boolean
  ) { }

  // constructor(args: Partial<Project | undefined>) {
  //   if (args) {
  //     Object.assign(this, args);
  //   }
  // }
};

export interface IPostDbUpdate {
  username: string;
  password: string;
  database: string;
  host: string;
  port: number;
  sqlScript: string;
  tunnelUsername?: string | null;
  tunnelUserPw?: string | null;
  tunnelDbUsername?: string | null;
  tunnelDbPw?: string | null;
  tunnel_port?: number | null;
  tunnel_user_name?: string | null;
  use_ssh_tunnel?: boolean | null;
  tunnel_destination_host?: string | null;
};

export interface IDbMeta {
  dbUser: string;
  dbUserPw: string;
  tunnelUserPw?: string | null;
  tunnelUsername?: string | null;
  tunnelDbPw?: string | null;
  tunnelDbUsername?: string | null;
}

export class DbMeta implements IDbMeta {

  constructor(
    public dbUser: string,
    public dbUserPw: string,
    public tunnelUserPw?: string | null,
    public tunnelUsername?: string | null,
    public tunnelDbPw?: string | null,
    public tunnelDbUsername?: string | null
  ) {

  }

}

export class PostDbUpdate implements IPostDbUpdate {

  static create(userDb: UserDb, updateMeta: UserDbToProcess, dbMeta: IDbMeta) {
    const { database_name, host, port, tunnel_port,
      tunnel_user_name, use_ssh_tunnel, tunnel_destination_host } = userDb;
    const { update_script } = updateMeta;
    const { dbUser, dbUserPw, tunnelUserPw, tunnelUsername, tunnelDbPw, tunnelDbUsername } = dbMeta;

    return new PostDbUpdate(
      dbUser,
      dbUserPw,
      database_name!,
      host!,
      port!,
      update_script,
      tunnelUsername,
      tunnelUserPw,
      tunnelDbUsername,
      tunnelDbPw,
      tunnel_port,
      tunnel_user_name,
      use_ssh_tunnel,
      tunnel_destination_host
    )
  }

  constructor(
    public username: string,
    public password: string,
    public database: string,
    public host: string,
    public port: number,
    public sqlScript: string,
    public tunnelUsername?: string | null,
    public tunnelUserPw?: string | null,
    public tunnelDbUsername?: string | null,
    public tunnelDbPw?: string | null,
    public tunnel_port?: number | null,
    public tunnel_user_name?: string | null,
    public use_ssh_tunnel?: boolean | null,
    public tunnel_destination_host?: string | null,
  ) { }
};

export interface IControlParams {
  db_major_version: number;
  db_minor_version: number;
  send_payment_notification?: boolean | null;
  app_base_url?: string | null;
  use_live_payment_portal?: boolean | null;
  param_company_name?: string | null;
  param_entity_name?: string | null;
  param_company_address?: string | null;
  param_abn?: string | null;
  param_acn?: string | null;
  param_primary_email?: string | null;
  param_additional_email?: string | null;
  param_website_url?: string | null;
  param_primary_contact_number?: string | null;
  param_additional_contact_number?: string | null;
  param_primary_image?: string | null;
  param_additional_image?: string | null;
  param_product_name?: string | null;
  param_reply_to_email?: string | null;
  param_reset_password_bcc?: string | null;
  param_text_api_host?: string | null;
  param_text_api_port?: number | null;
  param_text_api_path?: string | null;
  param_text_api_user?: string | null;
  param_text_api_key?: string | null;
};

export class ControlParams implements IControlParams {

  static create(args: IControlParams) {
    const {
      db_major_version, db_minor_version,
      send_payment_notification,
      app_base_url,
      use_live_payment_portal,
      param_company_name, param_entity_name,
      param_company_address,
      param_abn, param_acn,
      param_primary_email,
      param_additional_email,
      param_website_url,
      param_primary_contact_number,
      param_additional_contact_number,
      param_primary_image,
      param_additional_image,
      param_product_name,
      param_reply_to_email,
      param_reset_password_bcc,
      param_text_api_host,
      param_text_api_port,
      param_text_api_path,
      param_text_api_user,
      param_text_api_key
    } = args;

    return new ControlParams(
      db_major_version,
      db_minor_version,
      send_payment_notification,
      app_base_url,
      use_live_payment_portal,
      param_company_name, param_entity_name,
      param_company_address,
      param_abn, param_acn,
      param_primary_email,
      param_additional_email,
      param_website_url,
      param_primary_contact_number,
      param_additional_contact_number,
      param_primary_image,
      param_additional_image,
      param_product_name,
      param_reply_to_email,
      param_reset_password_bcc,
      param_text_api_host,
      param_text_api_port,
      param_text_api_path,
      param_text_api_user,
      param_text_api_key
    );

  }

  constructor(
    public db_major_version: number,
    public db_minor_version: number,
    public send_payment_notification?: boolean | null,
    public app_base_url?: string | null,
    public use_live_payment_portal?: boolean | null,
    public param_company_name?: string | null,
    public param_entity_name?: string | null,
    public param_company_address?: string | null,
    public param_abn?: string | null,
    public param_acn?: string | null,
    public param_primary_email?: string | null,
    public param_additional_email?: string | null,
    public param_website_url?: string | null,
    public param_primary_contact_number?: string | null,
    public param_additional_contact_number?: string | null,
    public param_primary_image?: string | null,
    public param_additional_image?: string | null,
    public param_product_name?: string | null,
    public param_reply_to_email?: string | null,
    public param_reset_password_bcc?: string | null,
    public param_text_api_host?: string | null,
    public param_text_api_port?: number | null,
    public param_text_api_path?: string | null,
    public param_text_api_user?: string | null,
    public param_text_api_key?: string | null,

  ) { }

}

/**
 * Copied from Kendo-svg-icons SVGIcon
 */
export interface ITrackerSVGIcon {
  /**
   * The unique name of the icon.
   */
  name: string;
  /**
   * The entire SVG content of the icon.
   */
  content: string;
  /**
   * The [viewBox](https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/viewBox)
   * definition that should be used for the icon.
   */
  viewBox: string;
}

export interface IStoryType {
  name: StoryTypes;
  icon: string; //IconName; // ITrackerSVGIcon
  iconClass: string;
}

export class StoryType implements IStoryType {

  constructor(
    public name: StoryTypes,
    public icon: string, //IconName, // string, // ITrackerSVGIcon
    public iconClass: string
  ) { }
}

export const storyTypeFactory = (): StoryType[] => {
  return [
    new StoryType(StoryTypes.Bug, 'bug', 'fa-bug'),
    new StoryType(StoryTypes.Feature, 'star', 'fa-star'),
    new StoryType(StoryTypes.Chore, 'gear', 'fa-gear'),
    new StoryType(StoryTypes.Release, 'flag-checkered', 'fa-checkered-flag')
  ]
};

export const defaultStoryTypes = storyTypeFactory();

export interface ICasiStoryType {
  id: number;
  user_story_type_description: string;
  icon?: string; //IconName; // ITrackerSVGIcon
  iconClass?: string;
}

export class CasiStoryType implements ICasiStoryType {
  id: number = 0;
  user_story_type_description: string = 'Not Set';
  icon?: string | undefined;
  iconClass?: string | undefined;
  // static create(partial: Partial<CasiStoryType>) {
  //   const { id, user_story_type_description } = partial;
  //   return new CasiStoryType(id!, user_story_type_description!);
  // }

  // constructor(
  //   public id: number,
  //   public user_story_type_description: string
  // ) { }
  constructor(partial: Partial<CasiStoryType>) {
    Object.assign(this, partial);
    let found = defaultStoryTypes.find((st) => st.name === this.user_story_type_description);
    if (found) {
      this.icon = found.icon;
      this.iconClass = found.iconClass;
    }
  }
}

export interface IStoryState {
  name: StoryStates;
}

export class StoryState implements IStoryState {
  constructor(public name: StoryStates) { }
}

export interface ICasiStoryStatusBase {
  id: number;
  user_story_status_description: string;
};

export interface ICasiStoryStatusOptions {
  nextUserStoryId: number;
  userStoryAction: string;
}
export interface ICasiStoryStatus extends ICasiStoryStatusBase {
  // id: number;
  // user_story_status_description: string;
  next_options: ICasiStoryStatusOptions[];
}
export interface ICasiStoryStatusParcel {
  original: ICasiStoryStatusBase;
  next_options: ICasiStoryStatusOptions
}

export class CasiStoryStatusParcel implements ICasiStoryStatusParcel {
  // original: ICasiStoryStatusBase;
  // next_options: ICasiStoryStatusOptions;

  constructor(public original: ICasiStoryStatusBase, public next_options: ICasiStoryStatusOptions) {
    // this.original = original;
    // this.next_options = next_options;
  }
}

export class CasiStoryStatus implements ICasiStoryStatus {
  static create(partial: Partial<CasiStoryStatus>) {
    const { id, user_story_status_description, next_options } = partial;
    return new CasiStoryStatus(id!, user_story_status_description!, next_options!);
  }

  constructor(
    public id: number,
    public user_story_status_description: string,
    public next_options: ICasiStoryStatusOptions[]
  ) { }
}

export interface IStoryPriority {
  name: StoryPriorities;
  // description: string;
}

export interface IStoryPoint {
  name: StoryPoints;
  icon?: ITrackerSVGIcon
};

export interface ICasiStoryPriority {
  id: number;
  user_story_priority_description: string;
}

export class CasiStoryPriority implements ICasiStoryPriority {
  static create(partial: Partial<CasiStoryPriority>) {
    const { id, user_story_priority_description } = partial;
    return new CasiStoryPriority(id!, user_story_priority_description!);
  }

  constructor(
    public id: number,
    public user_story_priority_description: string
  ) { }
}

export interface ICasiStoryEstimate {
  id: number;
  user_story_estimate_description: string;
}

export class CasiStoryEstimate implements ICasiStoryEstimate {
  static create(partial: Partial<CasiStoryEstimate>) {
    const { id, user_story_estimate_description } = partial;
    return new CasiStoryEstimate(id!, user_story_estimate_description!);
  }

  constructor(
    public id: number,
    public user_story_estimate_description: string
  ) { }
}

export interface ICasiProjectLabel {
  project_id: number;
  id?: number;
  project_label_description?: string;
  active?: boolean
}

export class CasiProjectLabel implements ICasiProjectLabel {
  static create(partial: Partial<CasiProjectLabel>) {
    const {
      project_id, id, project_label_description, active
    } = partial;
    return new CasiProjectLabel(project_id!, id, project_label_description, active);
  }

  constructor(
    public project_id: number,
    public id?: number,
    public project_label_description: string = '',
    public active?: boolean
  ) { }
}

export interface ICasiProjectUser {
  id?: number;
  project_id?: number;
  project_name?: string;
  user_id?: number;
  active?: boolean | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  selected: boolean;
  fullName?: string;
  initials?: string;
}

export class CasiProjectUser implements ICasiProjectUser {
  static createFromUserAndProject(user: User, project: Project) {
    return new CasiProjectUser({
      project_id: project.id,
      user_id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email
    });
  }

  id?: number;
  project_id?: number;
  project_name?: string;
  user_id?: number;
  active?: boolean | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  selected: boolean = false;

  constructor(partial: Partial<CasiProjectUser>) {
    Object.assign(this, partial);
  }

  get fullName() {
    if (this.first_name && this.last_name) {
      return `${this.first_name} ${this.last_name}`;
    } else if (this.first_name) {
      return this.first_name;
    } else if (this.last_name) {
      return this.last_name
    } else {
      return `fullName not set`;
    }
  }

  get initials() {
    if (this.first_name && this.last_name) {
      return `${this.first_name.charAt(0).toUpperCase()}${this.last_name.charAt(0).toLocaleUpperCase()}`;
    } else if (this.first_name) {
      return this.first_name.charAt(0).toUpperCase();
    } else if (this.last_name) {
      return this.last_name.charAt(0).toLocaleUpperCase()
    } else {
      return `NS`;
    }
  }


}

export class StoryPoint implements IStoryPoint {
  constructor(
    public name: StoryPoints,
    public icon?: ITrackerSVGIcon
  ) { }
}

export class StoryPriority implements IStoryPriority {
  constructor(
    public name: StoryPriorities,
    // public description: string
  ) { }
}

export interface IStoryLabel {
  name: StoryLabels,
  icon?: ITrackerSVGIcon
}

export class StoryLabel implements IStoryLabel {
  constructor(
    public name: StoryLabels,
    public icon?: ITrackerSVGIcon
  ) { }
}

export interface IUserStoryReview {
  name: UserStoryReviewTypes;
  reviewer?: number;
  status?: UserStoryReviewStatus;
}

export class UserStoryReview implements IUserStoryReview {
  constructor(
    public name: UserStoryReviewTypes,
    public reviewer?: number,
    public status?: UserStoryReviewStatus
  ) { }

}

export interface IUserStoryActivity {
  id: number;
  userStoryId: number;
  userId: number;
  description: string;
  userStoryActivityId?: number;
  attachments?: any[];
  dateTime?: Date;
  emoji?: any
};

export class UserStoryActivity implements IUserStoryActivity {
  id: number = 1;
  userStoryId: number = 1;
  userId: number = -7;
  description: string = 'Not set';
  userStoryActivityId?: number | undefined;
  attachments?: any[] | undefined;
  dateTime?: Date | undefined;
  emoji?: any | undefined;
  // constructor(
  //   public id: number,
  //   public userStoryId: number,
  //   public description: string,
  //   public userStoryActivityId?: number,
  //   public attachments?: any[]
  // ) {}
  constructor(partial: Partial<UserStoryActivity>) {
    Object.assign(this, partial);
  }
}

export interface IUserStory {
  id: number;
  projectId: number;
  title?: string;
  storyType?: StoryTypes;
  storyState?: StoryStates;
  storyPriority?: StoryPriorities;
  points?: IStoryPoint;
  requester: number;
  owners: number[];
  followThisStory?: string[];
  description?: string;
  lables?: IStoryLabel[];
  code?: string;
  tasks?: string[]
  activities?: IUserStoryActivity[]
}

export class UserStory implements IUserStory {

  id: number = 1;
  projectId: number = 1;
  title?: string;
  storyType?: StoryTypes;
  storyState?: StoryStates;
  storyPriority?: StoryPriorities;
  points?: IStoryPoint;
  requester: number = 1;
  owners: number[] = [];
  followThisStory?: string[];
  description?: string;
  lables?: IStoryLabel[];
  code?: string;
  tasks?: string[];
  activities?: IUserStoryActivity[];

  // constructor(
  //   public id: string,
  //   public projectId: number,
  //   public title?: string,
  //   public storyType?: string,
  //   public storyPriority?:string,
  //   public points?: string,
  //   public requester?:string,
  //   public owners?: string [],
  //   public followThisStory?: string [],
  //   public description?:string,
  //   public lables?: string[],
  //   public code?:string,
  //   public tasks?:string[]
  // ){}
  constructor(partial: Partial<UserStory>) {
    Object.assign(this, partial);
  }
};

export interface ICasiOwner {
  user_id: number;
  user_name: string;
  user_story_owner_id?: number
};

export class CasiOwner implements ICasiOwner {
  user_id: number = 1;
  user_name: string = '';
  user_story_owner_id?: number | undefined;

  constructor(partial: Partial<CasiOwner>) {
    Object.assign(this, partial);
  }
}

export interface ICasiUserStory {
  id?: number;
  project_id?: number;
  project_name?: string;
  title?: string;
  project_label_id?: number;
  project_label_description?: string;
  user_story_type_id?: number;
  user_story_type_description?: string;
  user_story_estimate_id?: number;
  user_story_estimate_description?: string;
  user_story_priority_id?: number;
  user_story_priority_description?: string;
  user_story_status_id?: number;
  user_story_status_description?: string;
  accepted_at?: Date;
  deadline?: Date;
  requested_by_user_id?: number;
  requested_by_user?: string;
  description?: string;
  updated_by_user_id?: number;
  updated_by_user?: string;
  owned_by_users_json?: ICasiOwner[];
  owned_by_users?: string;
  owned_by_user_ids?: number[];
  overrideUserIds: boolean;
  updatedAt?: Date;
  itemMode: ItemModes;
  sort_order?: number;
  previous_id?: number;
  next_id?: number;
}

export class CasiUserStory implements ICasiUserStory {

  static createWithDefaults(user?: User, project?: Project,
    storyStatus?: CasiStoryStatus[],
    storyTypes?: CasiStoryType[], storyPriorities?: CasiStoryPriority[],
    storyEstimates?: CasiStoryEstimate[]
  ) {
    let defaultStatus = setDefaultStatus(storyStatus)
    return new CasiUserStory({
      requested_by_user_id: user?.id || undefined,
      project_id: project?.id || undefined,
      project_name: project?.project_name || undefined,
      user_story_type_id: storyTypes ? storyTypes[2].id : undefined,
      user_story_estimate_id: storyEstimates ? storyEstimates[0].id : undefined,
      user_story_priority_id: storyPriorities ? storyPriorities[0].id : undefined,
      user_story_status_id: defaultStatus ? defaultStatus.id : undefined // storyStatus ? storyStatus[7].id : undefined
    })
  }

  id?: number;
  project_id?: number;
  project_name?: string;
  title?: string;
  project_label_id?: number;
  project_label_description?: string;
  user_story_type_id?: number;
  user_story_type_description?: string;
  user_story_estimate_id?: number;
  user_story_estimate_description?: string;
  user_story_priority_id?: number;
  user_story_priority_description?: string;
  user_story_status_id?: number;
  user_story_status_description?: string;
  accepted_at?: Date;
  deadline?: Date;
  requested_by_user_id?: number;
  requested_by_user?: string;
  description?: string;
  updated_by_user_id?: number;
  updated_by_user?: string;
  owned_by_users_json?: ICasiOwner[];
  owned_by_users?: string;
  owned_by_user_ids?: number[] = [];
  overrideUserIds: boolean = false;
  updatedAt?: Date;
  itemMode: ItemModes = ItemModes.UPDATE;
  sort_order?: number;
  previous_id?: number;
  next_id?: number;

  constructor(partial: Partial<CasiUserStory>) {
    Object.assign(this, partial);
    if (!this.overrideUserIds &&
      this.owned_by_users_json &&
      this.owned_by_users_json?.length > 0 &&
      this.owned_by_user_ids?.length === 0) {
      this.owned_by_user_ids = this.owned_by_users_json.map(o => o.user_id);
    }
    if (this.description)
      this.description = formatText(this.description);
    if (this.updatedAt) {
      this.updatedAt = new Date(this.updatedAt);
      // console.log(`type of updated At: `, typeof this.updatedAt);
    }
  }
};

export interface ICasiUserStoryComment {
  id?: number;
  user_story_id: number;
  user_id: number;
  first_name?: string;
  last_name?: string;
  comment?: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export class CasiUserStoryComment implements ICasiUserStoryComment {
  id: number | undefined;
  user_story_id: number = 1;
  user_id: number = 1;
  first_name?: string | undefined;
  last_name?: string | undefined;
  comment?: string | undefined;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;

  constructor(partial: Partial<CasiUserStoryComment>) {
    Object.assign(this, partial);
    if (this.comment)
      this.comment = formatText(this.comment);
  }

};

export enum DashboardStrings {
  ICEBOX = 'Icebox',
  CURRENT = 'Current',
  COMPLETED = 'Completed',
  DELETED = 'Deleted'
};

export interface IUserStoryOptions {
  dashboardStr?: DashboardStrings | null;
  projectId?: number | null;
  projectLabelId?: number | null;
  userStoryTypeId?: number | null;
  userStoryEstimate?: number | null;
  userStoryPriorityId?: number | null;
  requestedByUserId?: number | null;
  ownedByUserId?: number | null;
}

export class UserStoryOptions implements IUserStoryOptions {
  dashboardStr: DashboardStrings | null | undefined = null;
  projectId: number | null | undefined = null;
  projectLabelId: number | null | undefined = null;
  userStoryTypeId: number | null | undefined = null;
  userStoryEstimate: number | null | undefined = null;
  userStoryPriorityId: number | null | undefined = null;
  requestedByUserId: number | null | undefined = null;
  ownedByUserId: number | null | undefined = null;

  constructor(partial: Partial<UserStoryOptions>) {
    Object.assign(this, partial);
  }
};

export interface ICasiOwnerUser {
  user_id: number;
  fullName?: string;
  initials?: string;
}

export class CasiOwnerUser implements ICasiOwnerUser {
  user_id: number = 0;
  fullName?: string;
  initials?: string;

  static createFromUser(args: User) {
    return new CasiOwnerUser({
      user_id: args.id,
      fullName: args.fullName,
      initials: args.initials
    });
  }

  static createFromProjectUser(args: CasiProjectUser) {
    return new CasiOwnerUser({
      user_id: args.user_id,
      fullName: args.fullName,
      initials: args.initials
    });
  }
  constructor(partial: Partial<CasiOwnerUser>) {
    Object.assign(this, partial);
  }

}

export interface IRelease {
  id?: number,
  project_id?: number
  project_name?: string,
  released_by_user?: string,
  released_by_user_id?: number,
  release_date?: Date,
  comment?: string
};

export class Release implements IRelease {
  static create(args: any) {
    const {
      id, project_id, project_name, released_by_user,
      released_by_user_id, release_date, comment
    } = args;
    return new Release(id, project_id, project_name, released_by_user,
      released_by_user_id, release_date, comment);
  }
  constructor(
    public id?: number,
    public project_id?: number,
    public project_name?: string,
    public released_by_user?: string,
    public released_by_user_id?: number,
    public release_date?: Date,
    public comment?: string
  ) {
  }

};

export interface IProjectSummary {
  no_releases?: string,
  no_stories_released?: string,
  project_name?: string,
  project_id?: number,
  no_stories_accepted?: string,
  no_stories_delivered?: string,
  no_stories_finished?: string,
  no_stories_rejected?: string,
  no_stories_started?: string,
  no_unstarted?: string,
  no_stories_unscheduled?: string,
  no_deleted?: string
}

export class ProjectSummary implements IProjectSummary {
  static create(args: any) {
    const {
      no_releases, no_stories_released, project_name, project_id, no_stories_accepted, no_stories_delivered,
      no_stories_finished, no_stories_rejected, no_stories_started, no_unstarted,
      no_stories_unscheduled, no_deleted
    } = args;
    return new ProjectSummary(no_releases, no_stories_released, project_name, project_id, no_stories_accepted, no_stories_delivered,
      no_stories_finished, no_stories_rejected, no_stories_started, no_unstarted,
      no_stories_unscheduled, no_deleted);
  }
  constructor(
    public no_releases?: string,
    public no_stories_released?: string,
    public project_name?: string,
    public project_id?: number,
    public no_stories_accepted?: string,
    public no_stories_delivered?: string,
    public no_stories_finished?: string,
    public no_stories_rejected?: string,
    public no_stories_started?: string,
    public no_unstarted?: string,
    public no_stories_unscheduled?: string,
    public no_deleted?: string
  ) {
  }

};

export interface IEnquiry {
  id: number,
  first_name?: string,
  last_name?: string,
  email: string,
  phone?: string,
  subject?: string,
  message?: string,
  completed: boolean,
  user_id: number,
  createdAt: string
};

export class Enquiry implements IEnquiry {
  static create(args: any) {
    const {
      id, first_name, last_name,
      email, phone,
      subject, message, completed,
      user_id, createdAt
    } = args;
    return new Enquiry(
      id, email, completed, user_id,
      createdAt,
      first_name, last_name,
      phone, subject,
      message
    );
  }

  constructor(
    public id: number,
    public email: string,
    public completed: boolean,
    public user_id: number,
    public createdAt: string,
    public first_name?: string,
    public last_name?: string,
    public phone?: string,
    public subject?: string,
    public message?: string
  ) {
  }
};

export interface IEnquiryAddress {
  id?: number,
  address?: string,
};

export class EnquiryAddress implements IEnquiryAddress {
  static create(args: any) {
    const {
      id, address
    } = args;
    return new EnquiryAddress(
      id, address
    );
  }

  constructor(
    public id?: number,
    public address?: string
  ) { }
};


